import { createContext, useState } from "react";
import type { ReactNode } from "react";

import { LazyExperimentTester } from "@shared/components/ExperimentTester";
import { isProduction } from "@shared/utils";

type OverrideValues = { [experimentName: string]: string };
type Experiments = { [experimentName: string]: readonly string[] };

interface ExperimentContextData {
  roleId?: string;
  userId?: string;
  storeId?: string;
  analyticsAnonymousId?: string;
  overrideValues?: OverrideValues;
  setOverride?: (experimentName: string, group: string) => void;
  currentExperiments?: Experiments;
  setCurrentExperiments?: (experimentName: string, groups: readonly string[]) => void;
}

const ExperimentContext = createContext<ExperimentContextData>(null);

interface Props {
  children: ReactNode;
  value: ExperimentContextData;
}
const ExperimentProvider = ({ children, value }: Props) => {
  const [currentExperiments, setCurrentExperiments] = useState<Experiments>({});
  const [overrideValues, setOverrideValues] = useState<OverrideValues>({});

  const setOverride = (experimentName: string, group: string) => {
    if (isProduction()) return;
    if (overrideValues[experimentName] === group) return;

    setOverrideValues(previousValues => ({ ...previousValues, [experimentName]: group }));
  };

  const updateCurrentExperiments = (experimentName: string, groups: readonly string[]) => {
    if (isProduction()) return;
    if (currentExperiments[experimentName]) return;

    setCurrentExperiments(previousExperiments => ({
      ...previousExperiments,
      [experimentName]: groups,
    }));
  };

  return (
    <ExperimentContext.Provider
      value={{
        overrideValues,
        setOverride,
        currentExperiments,
        setCurrentExperiments: updateCurrentExperiments,
        ...value,
      }}
    >
      <LazyExperimentTester />
      {children}
    </ExperimentContext.Provider>
  );
};

export type { ExperimentContextData };
export { ExperimentContext, ExperimentProvider };
